import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { notify, money } from '../utility/Helper1';
import { useSelector, useDispatch } from 'react-redux';
import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import QRCode from "react-qr-code";

// Premium Lucide Icons
import {
    Users,
    Award,
    Share2,
    Copy,
    Check,
    Coins,
    TrendingUp,
    TrendingDown,
    ArrowUpRight,
    Search,
    UserCheck,
    Sparkles,
    RefreshCw,
    Link
} from "lucide-react";

// MUI icons
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

function TeamEarning() {
    const [loadingText, setLoadingText] = useState('Loading network...');
    const [loading, setLoading] = useState(false);
    const [downlinesLoading, setDownlinesLoading] = useState(false);
    const [viewingLevel, setViewingLevel] = useState(1);
    const [copiedCode, setCopiedCode] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [qrModalOpen, setQrModalOpen] = useState(false);

    const [refData, setRefData] = useState({ level1: [], level2: [], level3: [] });
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);

    const dispatch = useDispatch();
    const userData = useSelector(userSelect);

    const loadDownLines = (myrefid) => {
        setDownlinesLoading(true);
        dispatch(authActions.onRefreshSystemData(`user/referral/${myrefid}`, 10, {}, response => {
            setDownlinesLoading(false);
            if (response?.data?.referrals) {
                setRefData(response.data.referrals);
            }
        }));
    };

    useEffect(() => {
        if (userData?.myrefid) {
            loadDownLines(userData.myrefid);
        }
    }, [userData?.myrefid]);

    const processRefWithdrawal = () => {
        if (!finance?.[0]?.commission || parseFloat(finance[0].commission) <= 0) {
            notify('No claimable commission available', 0);
            return;
        }
        setLoading(true);
        setLoadingText('Receiving...');
        dispatch(authActions.onLogin(`withdraw/ref`, { currency: finance?.[0]?.type }, response => {
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    const getValueForMenu = (eachdownline, menuId, autoConfirm) => {
        const suffix = autoConfirm;
        let val = '0';
        switch (menuId) {
            case 1:
                val = eachdownline[`profitToday${suffix}`];
                break;
            case 2:
                val = eachdownline[`totalDeposit${suffix}`];
                break;
            case 3:
                val = eachdownline[`totalWithdrawal${suffix}`];
                break;
            default:
                val = '0';
        }
        // Format if numeric, otherwise return direct value
        return isNaN(val) ? val : money(parseFloat(val || 0), finance?.[0]?.type);
    };

    const currentData = refData[`level${viewingLevel}`] || [];
    const refLink = site_settings?.site_settings?.site_url + '/#/signup?ref=' + userData?.myrefid;

    const handleCopyCode = () => {
        setCopiedCode(true);
        notify('Referral code copied!', 1);
        setTimeout(() => setCopiedCode(false), 2000);
    };

    const handleCopyLink = () => {
        setCopiedLink(true);
        notify('Referral link copied!', 1);
        setTimeout(() => setCopiedLink(false), 2000);
    };

    // Premium Member Avatar Gradient Colors
    const avatarGradients = [
        "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)", // Blue
        "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)", // Purple
        "linear-gradient(135deg, #ec4899 0%, #db2777 100%)", // Pink
        "linear-gradient(135deg, #10b981 0%, #059669 100%)", // Green
        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", // Amber
        "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"  // Cyan
    ];

    const ref_earning_1 = site_settings?.plans?.[0]?.childs?.[0]?.ref_earning_1 || '10';
    const ref_earning_2 = site_settings?.plans?.[0]?.childs?.[0]?.ref_earning_2 || '5';
    const ref_earning_3 = site_settings?.plans?.[0]?.childs?.[0]?.ref_earning_3 || '2';

    const levelsMeta = [
        { level: 1, percent: `${ref_earning_1}%`, title: "Tier 1", label: "Direct Invites" },
        { level: 2, percent: `${ref_earning_2}%`, title: "Tier 2", label: "Indirect Invites" },
        { level: 3, percent: `${ref_earning_3}%`, title: "Tier 3", label: "General Network" }
    ];



    return (
        <SiteWide id="main" title="Affiliate Center">
            <Section loading={loading} overlayText={loadingText} title={'Affiliate Center'}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 24 }}>

                    {/* Page Header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                            <span style={{ color: "var(--text-muted)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Network & Rebates</span>
                            <h2 style={{ color: "var(--text-main)", fontSize: 20, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "4px 0 0" }}>Affiliate Center</h2>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {/* Refresh Button */}
                            <button
                                onClick={() => {
                                    if (userData?.myrefid) {
                                        loadDownLines(userData.myrefid);
                                    }
                                }}
                                disabled={downlinesLoading}
                                style={{
                                    width: 32, height: 32, borderRadius: "10px",
                                    background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.12)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", transition: "all 0.2s", color: "var(--text-muted)",
                                    outline: "none"
                                }}
                                onMouseEnter={e => {
                                    if (!downlinesLoading) {
                                        e.currentTarget.style.background = "rgba(59,130,246,0.12)";
                                        e.currentTarget.style.color = "var(--text-main)";
                                    }
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = "rgba(59,130,246,0.06)";
                                    e.currentTarget.style.color = "var(--text-muted)";
                                }}
                            >
                                <RefreshCw
                                    size={14}
                                    className={downlinesLoading ? "animate-spin text-blue-500" : ""}
                                    style={{ transition: "transform 0.5s ease" }}
                                />
                            </button>

                            <div style={{
                                background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.12)",
                                borderRadius: "12px", padding: "6px 12px", display: "flex", alignItems: "center", gap: 6
                            }}>
                                <Users size={14} className="text-blue-500" />
                                <span style={{ color: "var(--text-main)", fontSize: 11, fontWeight: 700 }}>
                                    {((refData.level1?.length || 0) + (refData.level2?.length || 0) + (refData.level3?.length || 0))} Team
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ── Referral Banner Card (Premium Glow Layout) ───────────────── */}
                    <div style={{
                        borderRadius: 20, padding: "22px 20px", position: "relative", overflow: "hidden",
                        background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #4f46e5 100%)",
                        boxShadow: "0 12px 30px -5px rgba(37,99,235,0.35)",
                        border: "1px solid rgba(99,102,241,0.25)"
                    }}>
                        <div style={{
                            position: "absolute", top: -40, right: -40, width: 150, height: 150,
                            borderRadius: "50%", background: "radial-gradient(circle, rgba(147,197,253,0.25), transparent)",
                            pointerEvents: "none"
                        }} />
                        <div style={{
                            position: "absolute", bottom: -50, left: -50, width: 180, height: 180,
                            borderRadius: "50%", background: "radial-gradient(circle, rgba(99,102,241,0.18), transparent)",
                            pointerEvents: "none"
                        }} />

                        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.14)",
                                display: "flex", alignItems: "center", justifyItems: "center", justifyContent: "center", marginBottom: 10,
                                border: "1.5px solid rgba(255,255,255,0.3)"
                            }}>
                                <Sparkles size={18} className="text-yellow-300 animate-pulse" />
                            </div>
                            <h3 style={{ color: "#ffffff", fontSize: 18, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "0 0 6px" }}>
                                Expand Your Network
                            </h3>
                            <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: "0 0 18px", maxWidth: "85%" }}>
                                Share your invite link and generate up to 3 levels of passive rebate commission on all active chat earnings.
                            </p>

                            {/* Referral ID Row + Small Clickable QR Code */}
                            <div style={{ display: "flex", gap: 8, width: "100%", marginBottom: 8 }}>
                                <div style={{
                                    display: "flex", alignItems: "center", gap: 8, flex: 1,
                                    background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.15)",
                                    borderRadius: 14, padding: "10px 12px", overflow: "hidden"
                                }}>
                                    <span style={{
                                        flex: 1, color: "#ffffff", fontSize: 13,
                                        fontFamily: "monospace", fontWeight: 700,
                                        letterSpacing: "0.06em", overflow: "hidden",
                                        textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        textAlign: "left"
                                    }}>
                                        {userData?.myrefid ?? '—'}
                                    </span>
                                    <CopyToClipboard text={userData?.myrefid ?? ''} onCopy={handleCopyCode}>
                                        <button style={{
                                            display: "flex", alignItems: "center", gap: 5,
                                            padding: "6px 12px", borderRadius: 8, border: "none",
                                            background: copiedCode ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.16)",
                                            cursor: "pointer", outline: "none", transition: "all 0.2s"
                                        }}>
                                            {copiedCode ? <Check size={13} className="text-green-400" /> : <Copy size={13} className="text-white" />}
                                            <span style={{ color: copiedCode ? "#4ade80" : "#ffffff", fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>
                                                {copiedCode ? "Copied" : "Copy Code"}
                                            </span>
                                        </button>
                                    </CopyToClipboard>
                                </div>

                                <div
                                    onClick={() => setQrModalOpen(true)}
                                    style={{
                                        width: 44, height: 44, borderRadius: 14, background: "#ffffff",
                                        padding: 4, display: "flex", alignItems: "center", justifyContent: "center",
                                        cursor: "pointer", border: "1.5px solid rgba(255,255,255,0.3)",
                                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)", transition: "all 0.2s",
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
                                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                >
                                    <QRCode value={refLink} size={36} style={{ display: "block" }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Claimable Commission Area ──────────────────────────────── */}
                    {/* <div style={{
                        background: "var(--card-bg, #ffffff)", border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                        boxShadow: "var(--card-shadow, 0 4px 18px rgba(59,130,246,0.04))", borderRadius: 20,
                        padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: 14,
                                background: "rgba(34,197,94,0.06)", border: "1.5px solid rgba(34,197,94,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center", color: "#22c55e"
                            }}>
                                <Coins size={20} />
                            </div>
                            <div>
                                <p style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>Affiliate Royalty</p>
                                <p style={{ color: "#22c55e", fontSize: 20, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "2px 0 0" }}>
                                    {money(finance?.[0]?.commission ?? 0, finance?.[0]?.type ?? 0)}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={processRefWithdrawal}
                            disabled={!finance?.[0]?.commission || parseFloat(finance[0].commission) <= 0}
                            style={{
                                background: (!finance?.[0]?.commission || parseFloat(finance[0].commission) <= 0)
                                    ? "rgba(59,130,246,0.06)"
                                    : "linear-gradient(135deg,#3b82f6,#2563eb)",
                                border: (!finance?.[0]?.commission || parseFloat(finance[0].commission) <= 0)
                                    ? "1px solid rgba(59,130,246,0.12)"
                                    : "none",
                                borderRadius: 14, padding: "10px 18px",
                                display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
                                color: (!finance?.[0]?.commission || parseFloat(finance[0].commission) <= 0) ? "var(--text-muted)" : "#ffffff",
                                fontSize: 12, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                boxShadow: (!finance?.[0]?.commission || parseFloat(finance[0].commission) <= 0) ? "none" : "0 4px 14px rgba(37,99,235,0.25)",
                                transition: "all 0.2s"
                            }}
                        >
                            <ArrowUpRight size={14} /> Claim
                        </button>
                    </div> */}

                    {/* ── Interactive Levels Switcher (Premium Segmented Tabs) ───── */}
                    <div>
                        <p style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px 4px" }}>
                            Commission Network Level
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {levelsMeta.map((lvl) => {
                                const count = refData[`level${lvl.level}`]?.length || 0;
                                const active = viewingLevel === lvl.level;
                                return (
                                    <div
                                        key={lvl.level}
                                        onClick={() => setViewingLevel(lvl.level)}
                                        style={{
                                            borderRadius: 16, padding: "12px 16px", cursor: "pointer",
                                            background: active
                                                ? "linear-gradient(135deg, rgba(37,99,235,0.06), rgba(99,102,241,0.03))"
                                                : "var(--card-bg, #ffffff)",
                                            border: active
                                                ? "1.5px solid rgba(37,99,235,0.25)"
                                                : "1.5px solid var(--border-color, rgba(59,130,246,0.08))",
                                            boxShadow: active
                                                ? "0 4px 16px rgba(37,99,235,0.05)"
                                                : "var(--card-shadow, 0 2px 12px rgba(59,130,246,0.02))",
                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <div style={{
                                                width: 36, height: 36, borderRadius: 10,
                                                background: active ? "#3b82f6" : "rgba(59,130,246,0.06)",
                                                color: active ? "#ffffff" : "#3b82f6",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                transition: "all 0.2s"
                                            }}>
                                                <Award size={18} />
                                            </div>
                                            <div>
                                                <p style={{ color: "var(--text-main)", fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif", margin: 0 }}>
                                                    {lvl.title} <span style={{ color: "#3b82f6", fontSize: 10, marginLeft: 4 }}>({lvl.percent} royalty)</span>
                                                </p>
                                                <p style={{ color: "var(--text-muted)", fontSize: 10, margin: 0 }}>{lvl.label}</p>
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{
                                                background: active ? "rgba(37,99,235,0.12)" : "var(--neutral-100, #f1f5f9)",
                                                color: active ? "#2563eb" : "var(--text-muted)",
                                                borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700
                                            }}>
                                                {count} {count === 1 ? 'member' : 'members'}
                                            </span>
                                            <div style={{
                                                width: 6, height: 6, borderRadius: "50%",
                                                background: active ? "#2563eb" : "transparent"
                                            }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>


                    {/* ── Members List Section ───────────────────────────────────── */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <p style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 0 4px" }}>
                                Level {viewingLevel} Team ({currentData.length})
                            </p>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                    Total Deposit
                                </span>
                                {downlinesLoading && currentData.length > 0 && (
                                    <span style={{
                                        display: "inline-flex", alignItems: "center", gap: 4,
                                        fontSize: 9, color: "#3b82f6", fontWeight: 700,
                                        background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.12)",
                                        borderRadius: 6, padding: "2px 6px"
                                    }}>
                                        <span className="animate-spin" style={{
                                            width: 8, height: 8, borderRadius: "50%",
                                            border: "1px solid #3b82f6", borderTopColor: "transparent"
                                        }} />
                                        Updating...
                                    </span>
                                )}
                            </div>
                        </div>

                        {downlinesLoading && currentData.length === 0 ? (
                            <div style={{
                                background: "var(--card-bg, #ffffff)", border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                borderRadius: 20, padding: "40px 20px", textAlign: "center",
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 12
                            }}>
                                <div className="animate-spin" style={{
                                    width: 28, height: 28, borderRadius: "50%",
                                    border: "3px solid rgba(59,130,246,0.1)", borderTopColor: "#3b82f6"
                                }} />
                                <p style={{ color: "var(--text-main)", fontSize: 12, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>Loading Team Members...</p>
                                <p style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>Retrieving downline network data</p>
                            </div>
                        ) : currentData.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, position: "relative" }}>
                                {downlinesLoading && (
                                    <div style={{
                                        position: "absolute", top: 0, right: 0, bottom: 0, left: 0,
                                        background: "var(--card-bg, #ffffff)", opacity: 0.4,
                                        zIndex: 10, pointerEvents: "none", borderRadius: 16
                                    }} />
                                )}
                                {currentData.map((member, index) => {
                                    const avatarBg = avatarGradients[index % avatarGradients.length];
                                    const activeWallet = finance?.[0];
                                    const suffix = activeWallet?.autoConfirm == 1 ? 1 : 0;

                                    return (
                                        <div key={index} style={{
                                            background: "var(--card-bg, #ffffff)", border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                            boxShadow: "var(--card-shadow, 0 2px 12px rgba(59,130,246,0.02))",
                                            borderRadius: 16, padding: "12px 14px",
                                            display: "flex", alignItems: "center", gap: 12
                                        }}>
                                            {/* Beautiful tailored Initial Avatar */}
                                            <div style={{
                                                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                                                background: avatarBg, display: "flex", alignItems: "center", justifyContent: "center",
                                                color: "#ffffff", fontSize: 14, fontWeight: 800, fontFamily: "'Syne',sans-serif",
                                                boxShadow: "0 4px 10px rgba(0,0,0,0.05)"
                                            }}>
                                                {member.username?.charAt(0)?.toUpperCase()}
                                            </div>

                                            {/* Member Username & Activity Indicator */}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <p style={{ color: "var(--text-main)", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                                        {member.username}
                                                    </p>
                                                    <span style={{
                                                        width: 6, height: 6, borderRadius: "50%",
                                                        background: "#22c55e", display: "inline-block",
                                                        boxShadow: "0 0 8px rgba(34,229,94,0.6)"
                                                    }} />
                                                </div>
                                                <p style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                                    Active Partner
                                                </p>
                                            </div>

                                            {/* Performance Metric values */}
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                                                <span style={{
                                                    background: suffix === 1 ? "rgba(16,185,129,0.08)" : "rgba(59,130,246,0.08)",
                                                    color: suffix === 1 ? "#10b981" : "#3b82f6",
                                                    borderRadius: 10, padding: "5px 10px",
                                                    fontSize: 11, fontWeight: 800, fontFamily: "'Syne',sans-serif",
                                                    border: suffix === 1 ? "1px solid rgba(16,185,129,0.15)" : "1px solid rgba(59,130,246,0.15)"
                                                }}>
                                                    {getValueForMenu(member, 2, suffix)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* Empty state card */
                            <div style={{
                                background: "var(--card-bg, #ffffff)", border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                borderRadius: 20, padding: 36, textAlign: "center",
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 12
                            }}>
                                <div style={{
                                    width: 56, height: 56, borderRadius: 16,
                                    background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.12)",
                                    display: "flex", alignItems: "center", justifyContent: "center", color: "#3b82f6"
                                }}>
                                    <GroupAddIcon style={{ fontSize: 24 }} />
                                </div>
                                <p style={{ color: "var(--text-main)", fontSize: 14, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>No Level {viewingLevel} Network Yet</p>
                                <p style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>Share your invite link above to start growing your affiliate system.</p>
                            </div>
                        )}
                    </div>

                    {/* ─── Premium QR Code Expansion Modal ─── */}
                    {qrModalOpen && (
                        <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                            <div onClick={() => setQrModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(8,11,17,0.8)", backdropFilter: "blur(8px)", transition: "all 0.3s" }} />
                            <div style={{
                                position: "relative", width: "100%", maxWidth: 360,
                                background: "var(--card-bg, #ffffff)", border: "1px solid var(--border-color)",
                                borderRadius: 24, padding: 24, boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)",
                                display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
                                animation: "zoomIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
                            }}>
                                <span style={{ color: "var(--text-muted)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Invitation Scan</span>
                                <h4 style={{ color: "var(--text-main)", fontSize: 18, fontWeight: 800, fontFamily: "'Syne', sans-serif", margin: "0 0 16px 0" }}>Share Invitation QR</h4>

                                <div style={{
                                    "--qr-bg": "#ffffff",
                                    background: "var(--qr-bg)",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: 20, padding: 18, display: "inline-block",
                                    boxShadow: "var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.05))",
                                    marginBottom: 16
                                }}>
                                    <QRCode value={refLink} size={180} />
                                </div>

                                <p style={{ color: "var(--text-muted)", fontSize: 11, lineHeight: 1.5, margin: "0 0 16px 0", padding: "0 10px" }}>
                                    Let your partner scan this code to register directly in your level 1 downline structure.
                                </p>

                                {/* Elegant Referral Link Copy Box */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%", marginBottom: 20, textAlign: "left" }}>
                                    <label style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 600, marginLeft: 4 }}>Referral Invitation Link</label>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        background: "var(--neutral-100, #f1f5f9)",
                                        border: "1px solid var(--border-color, rgba(59,130,246,0.08))",
                                        borderRadius: 14,
                                        padding: "6px 6px 6px 14px",
                                        width: "100%",
                                        boxSizing: "border-box"
                                    }}>
                                        <span style={{
                                            color: "var(--text-muted)",
                                            fontSize: 11,
                                            flex: 1,
                                            marginRight: 8,
                                            fontFamily: "monospace",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {refLink}
                                        </span>
                                        <CopyToClipboard text={refLink} onCopy={handleCopyLink}>
                                            <button
                                                style={{
                                                    border: "none",
                                                    background: copiedLink
                                                        ? "rgba(34,197,94,0.15)"
                                                        : "linear-gradient(135deg,#3b82f6,#2563eb)",
                                                    color: copiedLink ? "#22c55e" : "#ffffff",
                                                    borderRadius: 10,
                                                    padding: "8px 14px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    boxShadow: copiedLink ? "none" : "0 4px 12px rgba(37,99,235,0.2)",
                                                    transition: "all 0.2s",
                                                    outline: "none"
                                                }}
                                            >
                                                {copiedLink ? <Check size={12} className="text-green-500" /> : <Link size={12} />}
                                                <span>{copiedLink ? "Copied" : "Copy Link"}</span>
                                            </button>
                                        </CopyToClipboard>
                                    </div>
                                </div>

                                <button onClick={() => setQrModalOpen(false)} style={{
                                    width: "100%", borderRadius: 14, padding: "12px 0", border: "none",
                                    background: "linear-gradient(135deg,#3b82f6,#2563eb)",
                                    color: "#ffffff", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                    cursor: "pointer", boxShadow: "0 4px 14px rgba(37,99,235,0.2)"
                                }}>
                                    Close Window
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </Section>
        </SiteWide>
    );
}

export default TeamEarning;